import { DateTime } from "luxon";
import { z } from "zod/v3";
import { FormValues, ProductSchema } from "./validation/schema";
import {
  BackofficeEventsEventFeeInput,
  EventDefaultFee,
  EventFeeFormData,
  EventFeeInputData,
  EventFormData,
  PATEventAttributesFormData,
  PATEventAttributesInputData,
  RecruitmentEventAttributesFormData,
  RecruitmentEventAttributesInputData,
  RegistrationType,
  SessionFormData,
  SessionInputData
} from "./types";
import { camelCase } from "lodash";
import { toEventFeePaymentCollectionInput, type EventFeePaymentCollection } from "../eventFeeUtils";
import { getEventTypeForForm } from "../[eventSlug]/utils";

export const BACKOFFICE_TIMEZONE = "America/Chicago";

export const TERMS_CONTENT =
  "I have read and agree to the PGA of America [Terms and Privacy Policy](https://www.pga.com/pga-of-america/privacy-policy)";

const EMPTY_PAT_PRODUCT = {
  id: "",
  name: "",
  variantId: "",
  variantName: "",
  variantPrice: 0,
  collection: ""
};

export function getAvailableCustomFields(eventType?: string): Record<string, string> {
  return {
    date_of_birth: "Date of Birth",
    gender: "Gender",
    disclaimer: "Terms and Conditions",
    ...(eventType !== "PAT" && { additional_guests: "Additional Guests (Max 5)" })
  };
}

export function toNumber(value: any) {
  return value ? Number(value) : undefined;
}

export function convertToUTCDateTime(date: string, time: string, timezone: string = BACKOFFICE_TIMEZONE): string {
  const dateTimeString = `${date} ${time}`;
  const format = "yyyy-MM-dd h:mm a";

  const parsedDateTime = DateTime.fromFormat(dateTimeString, format, { zone: timezone });

  return parsedDateTime.isValid ? parsedDateTime.toUTC().toFormat("yyyy-MM-dd'T'HH:mm:ss'Z'") : "";
}

export function getDefaultSession(
  previousSession?: SessionFormData,
  timezone: string = BACKOFFICE_TIMEZONE
): SessionFormData {
  const now = DateTime.now().setZone(timezone);
  const hasPreviousSession = previousSession?.date.startDate;

  const sessionDate = hasPreviousSession
    ? DateTime.fromFormat(previousSession.date.startDate, "yyyy-MM-dd", { zone: timezone }).plus({ days: 1 })
    : now.plus({ months: 1 });

  const normalizedSessionDate = sessionDate.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });

  return {
    date: {
      startDate: normalizedSessionDate.toFormat("yyyy-MM-dd"),
      endDate: normalizedSessionDate.toFormat("yyyy-MM-dd")
    },
    startTime: previousSession?.startTime ?? "8:00 AM",
    endTime: previousSession?.endTime ?? "9:00 AM",
    maxAttendance: ""
  };
}

export function convertToDateTime(timeString: string): Date | null {
  const parsedTime = DateTime.fromFormat(timeString.trim(), "h:mm a");

  if (!parsedTime.isValid) {
    return null;
  }

  const today = DateTime.now().startOf("day");
  const dateTime = today.set({
    hour: parsedTime.hour,
    minute: parsedTime.minute
  });

  return dateTime.toJSDate();
}

export function getDisplayTimezone(geocodeResult: string | null, referenceDate?: string) {
  const timezone = geocodeResult ?? BACKOFFICE_TIMEZONE;
  const now = referenceDate ? DateTime.fromISO(referenceDate).setZone(timezone) : DateTime.now().setZone(timezone);
  return now.toFormat("ZZZZ");
}

export function getGlobalMaxAttendance(sessions: SessionFormData[]): string {
  return sessions.find(({ maxAttendance }) => !!maxAttendance)?.maxAttendance ?? "";
}

function getMaxAttendanceInput(maxAttendance?: string | null) {
  return maxAttendance ? Number(maxAttendance) : null;
}

export function transformSessionsToInput(
  sessions: FormValues["sessions"],
  timezone: string,
  globalMaxAttendance?: string
): SessionInputData[] {
  const hasMaxAttendanceOverride = globalMaxAttendance !== undefined;
  const maxAttendanceOverride = hasMaxAttendanceOverride ? getMaxAttendanceInput(globalMaxAttendance) : null;

  return sessions.map((session) => ({
    id: session.id,
    startsAt: convertToUTCDateTime(session.date.startDate, session.startTime, timezone),
    endsAt: convertToUTCDateTime(session.date.endDate, session.endTime, timezone),
    maxAttendance: hasMaxAttendanceOverride ? maxAttendanceOverride : getMaxAttendanceInput(session.maxAttendance)
  }));
}

export function getRegistrationMaxAttendanceOverride(
  data: Pick<FormValues, "registrationType" | "sessions" | "globalMaxAttendance">
) {
  if (data.registrationType !== "all") {
    return undefined;
  }

  if (data.sessions.length === 1) {
    return data.sessions[0]?.maxAttendance ?? "";
  }

  return data.globalMaxAttendance;
}

export function getDefaultRegistrationOpeningDate(): any {
  const registrationOpenDate = DateTime.now().setZone(BACKOFFICE_TIMEZONE).toFormat("yyyy-MM-dd");
  return {
    startDate: registrationOpenDate,
    endDate: registrationOpenDate
  };
}

export function getRecruitmentRegistrationDates(sessions: SessionFormData[]): {
  registrationOpenDate: { startDate: string; endDate: string };
  registrationCloseDate: { startDate: string; endDate: string };
} | null {
  const earliestSessionDate = getEarliestSessionDate(sessions);
  if (!earliestSessionDate) return null;

  const sessionDate = earliestSessionDate.toFormat("yyyy-MM-dd");
  const today = DateTime.now().setZone(BACKOFFICE_TIMEZONE).startOf("day");
  const openDate = earliestSessionDate < today ? sessionDate : today.toFormat("yyyy-MM-dd");

  return {
    registrationOpenDate: { startDate: openDate, endDate: openDate },
    registrationCloseDate: { startDate: sessionDate, endDate: sessionDate }
  };
}

export function getDefaultRegistrationClosingDate(
  sessions: SessionFormData[],
  eventType?: string,
  isEditMode?: boolean,
  originalRegistrationCloseDate?: any
): any {
  const startDates = sessions.map(({ date }) => DateTime.fromISO(date.startDate, { zone: BACKOFFICE_TIMEZONE }));
  const earliestDate = startDates.reduce((min, curr) => (curr < min ? curr : min));
  const isPAT = eventType === "PAT";

  if (isPAT) {
    const registrationCloseDate = earliestDate.minus({ weeks: 1 }).toFormat("yyyy-MM-dd");

    return {
      startDate: registrationCloseDate,
      endDate: registrationCloseDate
    };
  }

  if (isEditMode && originalRegistrationCloseDate) {
    const now = DateTime.now();
    const registrationClose = DateTime.fromISO(originalRegistrationCloseDate.startDate);

    if (now > registrationClose) {
      return originalRegistrationCloseDate;
    }
  }

  const registrationCloseDate = earliestDate
    .minus(eventType === "Recruitment" ? { days: 0 } : { days: 1 })
    .toFormat("yyyy-MM-dd");

  return {
    startDate: registrationCloseDate,
    endDate: registrationCloseDate
  };
}

export function setToDayBoundary(
  isoString: string,
  boundary: "start" | "end",
  timezone: string = BACKOFFICE_TIMEZONE
): string {
  const dateTime = DateTime.fromISO(isoString, { zone: timezone });
  const boundaryDateTime = boundary === "start" ? dateTime.startOf("day") : dateTime.endOf("day");
  return <string>boundaryDateTime.toUTC().toISO();
}

type Product = z.infer<typeof ProductSchema>;

export function getSelectedProducts(products: Product[]) {
  return products?.filter((product) => product.id && product.variantId);
}

export function transformDateString(
  dateString: string | undefined,
  timezone: string = BACKOFFICE_TIMEZONE
): { startDate: string; endDate: string } | null {
  if (!dateString) return null;

  const date = DateTime.fromISO(dateString).setZone(timezone).startOf("day");

  if (!date.isValid) {
    return null;
  }

  const formattedDate = date.toFormat("yyyy-MM-dd");
  return {
    startDate: formattedDate,
    endDate: formattedDate
  };
}

export function transformSessions(
  sessions: SessionInputData[],
  timezone: string = BACKOFFICE_TIMEZONE,
  isCopy: boolean = false
): SessionFormData[] {
  if (!sessions?.length) {
    return [getDefaultSession(undefined, timezone)];
  }

  const formattedSessions = sessions.map((session) => {
    const startDateTime = DateTime.fromISO(session.startsAt).setZone(timezone);
    const endDateTime = DateTime.fromISO(session.endsAt).setZone(timezone);

    return {
      ...(!isCopy && { id: session.id }),
      date: {
        startDate: startDateTime.toFormat("yyyy-MM-dd"),
        endDate: endDateTime.toFormat("yyyy-MM-dd")
      },
      startTime: startDateTime.toFormat("h:mm a"),
      endTime: endDateTime.toFormat("h:mm a"),
      maxAttendance: session.maxAttendance?.toString() || ""
    };
  });

  return formattedSessions;
}

export function getEarliestSession(sessions: SessionFormData[], timezone: string) {
  return sessions.reduce((earliest, current) => {
    const earliestDateTime = DateTime.fromFormat(
      `${earliest.date.startDate} ${earliest.startTime}`,
      "yyyy-MM-dd h:mm a",
      { zone: timezone }
    );

    const currentDateTime = DateTime.fromFormat(`${current.date.startDate} ${current.startTime}`, "yyyy-MM-dd h:mm a", {
      zone: timezone
    });

    return currentDateTime < earliestDateTime ? current : earliest;
  }, sessions[0]);
}

export function getEarliestSessionDate(sessions: SessionFormData[]): DateTime | undefined {
  if (!sessions.length) return undefined;
  const earliest = getEarliestSession(sessions, BACKOFFICE_TIMEZONE);
  const date = DateTime.fromISO(earliest.date.startDate, { zone: BACKOFFICE_TIMEZONE });
  return date.isValid ? date : undefined;
}

export function getPATRegistrationCloseMaxDate(sessions: SessionFormData[]): Date | undefined {
  return getEarliestSessionDate(sessions)?.minus({ weeks: 1 }).toJSDate();
}

export function getExpectedPATRegistrationCloseDate(sessions: SessionFormData[]): string | undefined {
  return getEarliestSessionDate(sessions)?.minus({ weeks: 1 }).toFormat("yyyy-MM-dd");
}

/**
 * PAT registration close is normally locked. Unlock it when the event is being
 * rescheduled in this form session, or when a prior reschedule left the close
 * date out of sync with the current session (exactly 7 days before).
 */
export function isPATRegistrationCloseEditable({
  isEditing,
  currentSessionDate,
  originalSessionDate,
  sessions,
  registrationCloseDate
}: {
  isEditing?: boolean;
  currentSessionDate?: string;
  originalSessionDate?: string;
  sessions: SessionFormData[];
  registrationCloseDate?: string;
}): boolean {
  if (!isEditing) return false;
  if (currentSessionDate !== originalSessionDate) return true;
  if (!registrationCloseDate) return true;

  const expectedCloseDate = getExpectedPATRegistrationCloseDate(sessions);
  if (!expectedCloseDate) return false;

  return registrationCloseDate !== expectedCloseDate;
}

export function toSessionDateTime(date: string, time: string, timezone: string) {
  const dateTime = DateTime.fromFormat(`${date} ${time}`, "yyyy-MM-dd h:mm a", { zone: timezone });
  if (!dateTime.isValid) {
    return null;
  }

  const tzAbbr = getDisplayTimezone(timezone, dateTime.toISO());
  const displayDateTime = dateTime.toFormat("LLL dd, yyyy h:mm a");
  return `${displayDateTime} (${tzAbbr})`;
}

const DEFAULT_BANNER_IMAGE_URL =
  "https://res.cloudinary.com/pgahq/image/upload/v1729016664/member-platform/mypga-event-banner.jpg";

export function transformPATAttributes(inputData?: PATEventAttributesInputData): PATEventAttributesFormData {
  const defaultPATAttributes: PATEventAttributesFormData = {
    yardageMale: "",
    courseRatingMale: "",
    yardageMaleFiftyPlus: "",
    yardageFemale: "",
    courseRatingFemale: "",
    yardageFemaleFiftyPlus: "",
    hostProfessional: { id: "", profilePhoto: "", name: "" },
    examiner: { id: "", profilePhoto: "", name: "" },
    courseConditions: "",
    problemsIssues: "",
    collection: "",
    registrationFeeCents: 0,
    courseFeeCents: 0
  };

  if (!inputData) {
    return defaultPATAttributes;
  }

  return {
    yardageMale: inputData.yardageMale?.toString() ?? "",
    courseRatingMale: inputData.courseRatingMale?.toString() ?? "",
    yardageMaleFiftyPlus: inputData.yardageMaleFiftyPlus?.toString() ?? "",
    yardageFemale: inputData.yardageFemale?.toString() ?? "",
    courseRatingFemale: inputData.courseRatingFemale?.toString() ?? "",
    yardageFemaleFiftyPlus: inputData.yardageFemaleFiftyPlus?.toString() ?? "",
    hostProfessional: {
      id: inputData.hostProfessionalId ?? "",
      profilePhoto: "",
      name: ""
    },
    examiner: {
      id: inputData.examinerId ?? "",
      profilePhoto: "",
      name: ""
    },
    courseConditions: inputData.courseConditions ?? "",
    problemsIssues: inputData.problemsIssues ?? ""
  };
}

export function transformRecruitmentAttributes(
  inputData?: RecruitmentEventAttributesInputData
): RecruitmentEventAttributesFormData {
  const defaultRecruitmentAttributes: RecruitmentEventAttributesFormData = {
    recruitmentEventType: "",
    purpose: "",
    strategicFunding: "",
    pgaRepresentation: [],
    supportRequested: [],
    estimatedExpenses: undefined,
    sponsorshipAsk: undefined,
    estimatedParents: "",
    estimatedCoaches: "",
    estimatedOtherAttendees: "",
    staffAttendees: [],
    totalAttendees: "",
    additionalComments: "",
    eventRecap: "",
    overallFeedback: "",
    eventRating: "",
    commentsAndQuotes: "",
    photoUploads: [],
    isFinalized: false
  };

  if (!inputData) {
    return defaultRecruitmentAttributes;
  }

  const result = {
    recruitmentEventType: inputData.recruitmentEventType?.toUpperCase() ?? "",
    purpose: inputData.purpose?.toUpperCase() ?? "",
    strategicFunding: inputData.strategicFunding?.toUpperCase() ?? "",
    pgaRepresentation: inputData.pgaRepresentation?.map((value) => value.toUpperCase()) ?? [],
    supportRequested: inputData.supportRequested?.map((value) => value.toUpperCase()) ?? [],
    estimatedExpenses: inputData.estimatedExpenses ?? 0,
    sponsorshipAsk: inputData.sponsorshipAsk ?? 0,
    estimatedParents: inputData.estimatedParents?.toString() ?? "",
    estimatedCoaches: inputData.estimatedCoaches?.toString() ?? "",
    estimatedOtherAttendees: inputData.estimatedOtherAttendees?.toString() ?? "",
    totalAttendees: "",
    additionalComments: inputData.additionalComments ?? "",
    eventRecap: inputData.eventRecap ?? "",
    overallFeedback: inputData.overallFeedback ?? "",
    eventRating: inputData.eventRating?.toString() ?? "",
    commentsAndQuotes: inputData.commentsAndQuotes ?? "",
    photoUploads: inputData.photoUploads ?? [],
    isFinalized: inputData.isFinalized ?? false
  };

  return result;
}

export function getDefaultRegistrationType(event?: EventFormData): RegistrationType {
  const eventTypeName = event?.eventType?.name ?? event?.activityType?.title;
  return eventTypeName === "PAT" ? "all" : "individual";
}

export function isSeminarFormEvent({
  eventType,
  orderTemplateId,
  eventFees
}: {
  eventType?: { name?: string };
  orderTemplateId?: string | null;
  eventFees?: EventFeeFormData[];
}) {
  return !!eventType?.name?.includes("Seminar") || !!orderTemplateId || (eventFees?.length ?? 0) > 0;
}

export function getEventTypeInput(eventType: FormValues["eventType"]) {
  return eventType.source === "legacyEventType" ? { eventTypeId: eventType.id } : { activityTypeId: eventType.id };
}

export function getDefaultFormValues(event?: EventFormData, isCopy?: boolean): FormValues {
  const defaultSession = getDefaultSession();

  if (!event) {
    return {
      eventName: "",
      eventType: { id: "", name: "" },
      section: "",
      sectionId: "",
      legacyId: "",
      universityIds: [],
      orderTemplateId: "",
      locationType: "onsite",
      facility: "",
      facilityId: "",
      facilityName: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      zipcode: "",
      timezone: BACKOFFICE_TIMEZONE,
      location: "",
      description: "",
      sessions: [defaultSession],
      registrationType: getDefaultRegistrationType(),
      globalMaxAttendance: "",
      bannerImageUrl: DEFAULT_BANNER_IMAGE_URL,
      recruitmentLeadFormUrl: "",
      registrationOpenDate: getDefaultRegistrationOpeningDate(),
      registrationCloseDate: getDefaultRegistrationClosingDate([defaultSession]),
      waitlistType: "disabled",
      customFields: [],
      termsContent: "",
      products: [EMPTY_PAT_PRODUCT],
      eventFees: [],
      collectsAttendance: false,
      draft: false,
      patEventAttributes: transformPATAttributes(),
      recruitmentEventAttributes: transformRecruitmentAttributes()
    };
  }

  const transformedRecruitmentAttributes = transformRecruitmentAttributes(event.recruitmentEventAttributes);

  const { id: eventTypeId, name: eventTypeName, source: eventTypeSource } = getEventTypeForForm(event);
  const transformedSessions = transformSessions(event.sessions ?? [], event.timezone, isCopy);
  const registrationType = (
    event.registrationType ? event.registrationType.toLowerCase() : getDefaultRegistrationType(event)
  ) as RegistrationType;
  return {
    eventName: isCopy ? `${event.title} (Copy)` : (event.title ?? ""),
    eventType: {
      id: eventTypeId,
      name: eventTypeName,
      source: eventTypeSource
    },
    section: event.section?.name ?? "",
    sectionId: event.section?.id ?? "",
    legacyId: event.section?.legacyId ?? "",
    universityIds: event.universities?.map((university) => university.id) ?? [],
    orderTemplateId: event.orderTemplateId ?? "",
    locationType: event.locationType?.toLowerCase() === "virtual" ? "virtual" : "onsite",
    facility: event.facility?.name ?? "",
    facilityId: event.facility?.id ?? "",
    facilityName: event.facility?.name ?? "",
    address1: event.address1 ?? "",
    address2: event.address2 ?? "",
    city: event.city ?? "",
    state: event.state ?? "",
    zipcode: event.zip ?? "",
    timezone: event.timezone ?? BACKOFFICE_TIMEZONE,
    location: event.location ?? "",
    description: event.description ?? "",
    sessions: transformedSessions,
    registrationType,
    globalMaxAttendance: registrationType === "all" ? getGlobalMaxAttendance(transformedSessions) : "",
    bannerImageUrl: event.bannerImageUrl ?? DEFAULT_BANNER_IMAGE_URL,
    recruitmentLeadFormUrl: event.recruitmentLeadFormUrl ?? "",
    registrationOpenDate:
      transformDateString(event.registrationOpenAt, event.timezone) ?? getDefaultRegistrationOpeningDate(),
    registrationCloseDate:
      transformDateString(event.registrationCloseAt, event.timezone) ??
      getDefaultRegistrationClosingDate([defaultSession], eventTypeName),
    waitlistType: event.hasWaitlist ? "notification" : "disabled",
    customFields: event.customFields ?? [],
    termsContent: event?.termsContent ?? "",
    products: transformProducts(event?.patEventAttributes),
    eventFees: transformEventFees(event.eventFees, isCopy),
    collectsAttendance: event.collectsAttendance ?? false,
    draft: event.draft ?? false,
    patEventAttributes: transformPATAttributes(event.patEventAttributes),
    recruitmentEventAttributes: transformedRecruitmentAttributes
  };
}

export function buildSeminarSessions(
  templates: ReadonlyArray<{ startsAt: string; endsAt: string; maxAttendance: number }>
): SessionFormData[] {
  return templates.map((template) => {
    const start = template.startsAt
      ? DateTime.fromISO(template.startsAt, { zone: "UTC" }).setZone(BACKOFFICE_TIMEZONE)
      : null;
    const end = template.endsAt
      ? DateTime.fromISO(template.endsAt, { zone: "UTC" }).setZone(BACKOFFICE_TIMEZONE)
      : null;
    const sessionDate = start?.isValid ? start.toFormat("yyyy-MM-dd") : "";
    const maxAttendance = template.maxAttendance ? String(template.maxAttendance) : "";

    return {
      date: { startDate: sessionDate, endDate: sessionDate },
      startTime: start?.isValid ? start.toFormat("h:mm a") : "",
      endTime: end?.isValid ? end.toFormat("h:mm a") : "",
      maxAttendance
    };
  });
}

function parseAmountCents(value?: string | number | null) {
  if (typeof value === "number") {
    return value;
  }

  if (!value) {
    return 0;
  }

  const parsedAmount = parseInt(value, 10);
  return Number.isNaN(parsedAmount) ? 0 : parsedAmount;
}

export function mapFeeDefaultsToEventFees(feeDefaults: EventDefaultFee[]): EventFeeFormData[] {
  return feeDefaults.map(({ productVariant }) => ({
    kind: "catalog" as const,
    paymentCollection: "registration" as const,
    productVariantId: productVariant.id,
    productVariantName: productVariant.name,
    amountCents: parseAmountCents(productVariant.priceCents),
    displayName: ""
  }));
}

export function transformEventFees(eventFees?: EventFeeInputData[], isCopy = false): EventFeeFormData[] {
  if (!eventFees?.length) {
    return [];
  }

  return eventFees.map((eventFee) => {
    const isExternalFee = !eventFee.productVariant;
    const amountCents = isExternalFee
      ? parseAmountCents(eventFee.amountCents)
      : parseAmountCents(eventFee.productVariant?.priceCents);

    return {
      ...(!isCopy && eventFee.id ? { id: eventFee.id } : {}),
      kind: isExternalFee ? "external" : "catalog",
      paymentCollection: isExternalFee ? "external_system" : "registration",
      productVariantId: eventFee.productVariant?.id ?? "",
      productVariantName: eventFee.productVariant?.name ?? "",
      amountCents,
      displayName: eventFee.displayName ?? ""
    };
  });
}

function hasCatalogEventFee(row: EventFeeFormData) {
  return row.kind === "catalog" && row.productVariantId;
}

function hasExternalEventFee(row: EventFeeFormData) {
  return row.kind === "external" && row.displayName.trim().length > 0 && row.amountCents > 0;
}

export function buildEventFeeInputs(eventFees: EventFeeFormData[]): BackofficeEventsEventFeeInput[] {
  return eventFees
    .filter((row) => hasCatalogEventFee(row) || hasExternalEventFee(row))
    .map((row) => {
      const paymentCollection: EventFeePaymentCollection = row.kind === "external" ? "external_system" : "registration";

      return {
        ...(row.id ? { id: row.id } : {}),
        paymentCollection: toEventFeePaymentCollectionInput(paymentCollection),
        ...(row.kind === "external"
          ? {
              displayName: row.displayName.trim(),
              amountCents: row.amountCents
            }
          : {
              productVariantId: row.productVariantId
            })
      };
    });
}

export function transformProducts(inputData?: PATEventAttributesInputData) {
  const registrationFeeCents = inputData?.registrationFeeCents || 0;
  const collection = inputData?.collectionType;
  const courseFeeCents = inputData?.courseFeeCents;

  const products = [
    {
      id: "",
      name: "",
      variantId: "",
      variantName: "",
      variantPrice: registrationFeeCents,
      collection: "registration"
    }
  ];

  if (courseFeeCents) {
    products.push({
      id: "",
      name: "",
      variantId: "",
      variantName: "",
      variantPrice: courseFeeCents,
      collection: camelCase(collection) || ""
    });
  }

  return products;
}
